import { useEffect, useState, useCallback, useRef } from "react";
import { useGameStore } from "./state/useGameStore";
import { GameRoot } from "./components/GameRoot";
import { TitleScreen } from "./components/TitleScreen";
import { PhaserRoot } from "./phaser/PhaserRoot";
import type { VnProgram } from "./engine/Interpreter";
import type { AchievementDef } from "./engine/AchievementEngine";
import { SaveLoad } from "./engine/SaveLoad";
import { Rollback } from "./engine/Rollback";
import { useHotReload } from "./hooks/useHotReload";
import { platform } from "./platform";
import type { CatalogueEntry } from "./platform/PlatformAdapter";
import { ResetPasswordPage } from "./components/ResetPasswordPage";
import { VerifyEmailPage } from "./components/VerifyEmailPage";

const storyParam =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("story")
    : null;

const pathname = typeof window !== "undefined" ? window.location.pathname : null;
const tokenParam =
  typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("token") : null;

export function App() {
  if (pathname === "/reset-password" && tokenParam) {
    return <ResetPasswordPage token={tokenParam} />;
  }
  if (pathname === "/verify-email" && tokenParam) {
    return <VerifyEmailPage token={tokenParam} />;
  }

  const [currentScriptName, setCurrentScriptName] = useState<string | null>(storyParam);
  const [program, setProgram] = useState<VnProgram | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const screen = useGameStore((s) => s.screen);
  const loadProgram = useGameStore((s) => s.loadProgram);
  const bb = useGameStore((s) => s.bb);
  const bus = useGameStore((s) => s.bus);
  const interpreter = useGameStore((s) => s.interpreter);
  const achievementUnlocked = useGameStore((s) => s.achievementUnlocked);
  const catalogue = platform.useCatalogue();
  const [preludeEntry, setPreludeEntry] = useState<CatalogueEntry | null>(null);

  const rollbackRef = useRef<Rollback | null>(null);
  const saveLoadRef = useRef<SaveLoad | null>(null);

  // Prelude (req Module 12) is reachable even fully logged out, so it never
  // shows up in the session-gated useCatalogue() list for an anonymous
  // visitor — fetched separately here so handleNewGame can still find its
  // url_params regardless of login state.
  useEffect(() => {
    let cancelled = false;
    platform.fetchPrelude().then((entry) => {
      if (!cancelled) setPreludeEntry(entry);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize or re-initialize SaveLoad (and the session heartbeat) when program changes
  useEffect(() => {
    if (!program) {
      rollbackRef.current = null;
      saveLoadRef.current = null;
      return;
    }
    const bbState = useGameStore.getState().bb;
    const rollback = new Rollback(interpreter, bbState, null, bus);
    rollbackRef.current = rollback;
    saveLoadRef.current = new SaveLoad(
      rollback,
      interpreter,
      bus,
      platform.createSaveStorage(currentScriptName ?? ""),
    );
    const disconnectHeartbeat = platform.connectHeartbeat(currentScriptName ?? "", () => {
      useGameStore.getState().returnToTitle();
    });
    return () => {
      disconnectHeartbeat();
    };
  }, [program, interpreter, bus, currentScriptName]);

  const handleReload = useCallback((p: VnProgram) => setProgram(p), []);
  useHotReload({ interpreter, bb, bus, scriptName: currentScriptName ?? "", onReload: handleReload });

  // Fetch script when currentScriptName changes
  useEffect(() => {
    if (!currentScriptName) {
      setProgram(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    platform
      .loadGame(currentScriptName)
      .then((data: VnProgram) => {
        if (data.format === "vn-program-error") {
          const errors = (data as Record<string, unknown>).errors as Array<{ msg: string }>;
          setError(errors?.map((e) => e.msg).join("\n") ?? "Unknown compile error");
          setProgram(null);
          return;
        }
        setProgram(data);

        // Update URL query param without reloading page
        const url = new URL(window.location.href);
        url.searchParams.set("story", currentScriptName);
        window.history.replaceState({}, "", url.toString());
      })
      .catch((err) => {
        setError(`Failed to load script: ${err}`);
        setProgram(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentScriptName]);

  const handleNewGame = useCallback(() => {
    if (!program) return;
    const entry =
      catalogue.find((g) => g.id === currentScriptName) ??
      (preludeEntry?.id === currentScriptName ? preludeEntry : undefined);
    loadProgram(program, currentScriptName ?? "", entry?.urlParams);
  }, [program, loadProgram, currentScriptName, catalogue, preludeEntry]);

  const handleLoadComplete = useCallback(() => {
    useGameStore.setState({ screen: "playing", overlay: "none" });
  }, []);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
      {/* Always mounted so the canvas container stays in the DOM (Scale Manager stays valid).
          The Phaser game itself is deferred until the first game start so the AudioContext
          is created close to a user gesture. */}
      <PhaserRoot bus={bus} interpreter={interpreter} bb={bb} rollback={rollbackRef.current} ready={screen === "playing"} />

      {screen === "title" ? (
        <TitleScreen
          title={program?.version ?? "ArcTree Story"}
          saveLoad={saveLoadRef.current}
          program={program}
          selectedScript={currentScriptName}
          onSelectScript={setCurrentScriptName}
          onNewGame={handleNewGame}
          onLoadComplete={handleLoadComplete}
          error={error}
          loading={loading}
          achievements={(program?.globals?.achievements ?? {}) as Record<string, AchievementDef>}
          achievementUnlocked={achievementUnlocked}
          preludeEntry={preludeEntry}
        />
      ) : !program ? (
        <main
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#eee",
            background: "#0a0a12",
          }}
        >
          Loading story data...
        </main>
      ) : (
        <GameRoot
          program={program}
          rollback={rollbackRef.current}
          saveLoad={saveLoadRef.current}
        />
      )}
    </div>
  );
}
